import React, { useState } from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import { PolicyMultiSelect } from './PolicyMultiSelect';
import { ApprovedDomain, DomainAssignment } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  domainListContainer: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
  },
  domainListHeader: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
  },
  domainList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    maxHeight: '200px',
    overflowY: 'auto',
  },
  domainItem: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  domainName: {
    fontWeight: '600',
    fontFamily: 'monospace',
    color: tokens.colorNeutralForeground1,
  },
  orgName: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  policySection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  helperText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    ...shorthands.padding('16px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

interface QuickAssignmentProps {
  domains: ApprovedDomain[];
  onBack: () => void;
  onComplete: (assignments: DomainAssignment[]) => void;
}

export const QuickAssignment: React.FC<QuickAssignmentProps> = ({
  domains,
  onBack,
  onComplete,
}) => {
  const styles = useStyles();
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const getDomainTestIdSegment = (domain: string) => domain.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-)|(-$)/g, '');

  const handleComplete = () => {
    if (selectedPolicies.length === 0) return;

    // Create assignments for all domains with the same policies
    const assignments: DomainAssignment[] = domains.map(domain => ({
      domainId: domain.id,
      domainName: domain.domain,
      policyIds: selectedPolicies,
      status: 'pending',
    }));

    onComplete(assignments);
  };

  return (
    <div className={styles.container} data-testid="quick-assignment">
      <div className={styles.helperText} data-testid="quick-assignment-helper-text-container">
        <Text data-testid="quick-assignment-helper-text">
          Selected domains will be added to the policies you choose below. This is the quickest way to assign multiple domains at once. 
        </Text>
      </div>

      <div className={styles.domainListContainer} data-testid="quick-assignment-domains">
        <Text className={styles.domainListHeader} data-testid="quick-assignment-domains-header">
          Assigning {domains.length} domain{domains.length !== 1 ? 's' : ''}:
        </Text>
        <div className={styles.domainList} data-testid="quick-assignment-domain-list">
          {domains.map((domain, index) => (
            <div key={domain.id || index} className={styles.domainItem} data-testid={`quick-assignment-domain-item-${getDomainTestIdSegment(domain.domain)}`}>
              <span className={styles.domainName} data-testid={`quick-assignment-domain-name-${getDomainTestIdSegment(domain.domain)}`}>{domain.domain}</span>
              {domain.organization && (
                <span className={styles.orgName} data-testid={`quick-assignment-domain-organization-${getDomainTestIdSegment(domain.domain)}`}>({domain.organization})</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.policySection} data-testid="quick-assignment-policy-section">
        <Text className={styles.sectionTitle} data-testid="quick-assignment-policy-section-title">Select Policies</Text>
        <PolicyMultiSelect
          selectedPolicyIds={selectedPolicies}
          onSelectionChange={setSelectedPolicies}
          placeholder="Choose which policies to add these domains to..."
        />
      </div>

      <div className={styles.actions} data-testid="quick-assignment-actions">
        <Button
          appearance="subtle"
          onClick={onBack}
          icon={<ArrowLeft20Regular data-testid="quick-assignment-back-icon" />}
          data-testid="quick-assignment-back"
        >
          Back to Mode Selection
        </Button>

        <Button
          appearance="primary"
          onClick={handleComplete}
          icon={<Checkmark20Regular data-testid="quick-assignment-review-apply-icon" />}
          iconPosition="after"
          disabled={selectedPolicies.length === 0}
          data-testid="quick-assignment-review-apply"
        >
          Review & Apply ({selectedPolicies.length} {selectedPolicies.length === 1 ? 'policy' : 'policies'})
        </Button>
      </div>
    </div>
  );
};
